import { ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import Header from '../Header/Header';
import Sidebar from '../Sidebar/Sidebar';
import { useSidebarStore } from '../../stores/SidebarStore';
import AgreementsAcceptModal from '../AgreementsAcceptModal/AgreementsAcceptModal';
import { useAgreementsValidation } from 'src/utils/hooks/useAgreementsValidation';
import InactivityPrompt from '../InactivityPrompt/InactivityPrompt';
import MaintenanceBanner from '../MaintenanceBanner/MaintenanceBanner';
import { useGetInstanceConfig } from 'src/shared/queries/useGetInstanceConfig';
import { Themes, useThemeStore } from 'src/stores/ThemeStore';
import { useImmigrationInstanceStore } from 'src/stores/ImmigrationInstanceStore';
import styles from './Layout.module.scss';

interface LayoutProps {
  children: ReactNode;
}

// Routes that should not have header, sidebar
const EXCLUDED_LAYOUT_ROUTES = ['/questionnaireprintview'];

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { isCollapsed } = useSidebarStore();
  const location = useLocation();
  const {
    agreementsToAccept,
    isAgreementsModalVisible,
    shouldShowPageContent,
    closeAgreementsModal,
    handleDeclineAgreements,
    handleAcceptAgreements,
  } = useAgreementsValidation();
  const { data: instanceConfig } = useGetInstanceConfig();
  const { setImmigrationInstance } = useImmigrationInstanceStore();
  const { setTheme } = useThemeStore();
  const isMinimalLayout = EXCLUDED_LAYOUT_ROUTES.some((route) => location.pathname.startsWith(route));

  useEffect(() => {
    if (!instanceConfig) return;

    const isDeloitte = (instanceConfig?.immigrationInstance || 1) !== 1;
    setTheme(isDeloitte ? Themes.deloitte : Themes.default);
    setImmigrationInstance(instanceConfig);
  }, [instanceConfig, setTheme, setImmigrationInstance]);

  const commonModals = (
    <>
      {agreementsToAccept.length > 0 && (
        <AgreementsAcceptModal
          visible={isAgreementsModalVisible}
          onModalClosed={closeAgreementsModal}
          agreements={agreementsToAccept}
          onAgreementDeclined={handleDeclineAgreements}
          onAgreementsAccepted={handleAcceptAgreements}
        />
      )}
      <InactivityPrompt />
    </>
  );

  // For minimal layout routes, render only children without header, sidebar, and wrapper class
  if (isMinimalLayout) {
    return (
      <div>
        {children}
        {commonModals}
      </div>
    );
  }

  return (
    <div className={styles['layout-wrapper']} id="layout-wrapper">
      {shouldShowPageContent && (
        <>
          <MaintenanceBanner />
          <Header />
          <Sidebar />
          <main className={classNames(styles['main-container'], { [styles['main-container-collapsed']]: isCollapsed })}>
            {children}
          </main>
        </>
      )}
      {commonModals}
    </div>
  );
};

export default Layout;
